import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import type { Employee } from '@/types/chat';
import { memo } from 'react';

interface ChatUserListProps {
  users: Employee[];
  selectedUser: Employee | null;
  onSelectUser: (user: Employee) => void;
  onlineUsers: Set<string>;
  unreadCounts: Record<string, number>;
}

// Memoize individual user item for better performance
const ChatUserItem = memo(
  ({
    user,
    isSelected,
    isOnline,
    unreadCount,
    onSelect,
  }: {
    user: Employee;
    isSelected: boolean;
    isOnline: boolean;
    unreadCount: number;
    onSelect: () => void;
  }) => (
    <button
      onClick={onSelect}
      className={`w-full p-4 flex items-center gap-3 hover:bg-gray-100 transition-colors ${
        isSelected ? 'bg-gray-100' : ''
      }`}
    >
      <div className="relative">
        <Avatar>
          <AvatarImage src={user.empAvatar || ''} />
          <AvatarFallback>{user.empName.charAt(0)}</AvatarFallback>
        </Avatar>
        <span
          className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white ${
            isOnline ? 'bg-green-500' : 'bg-gray-300'
          }`}
        />
      </div>
      <div className="flex-1 text-left">
        <div className="flex justify-between items-center">
          <span className="font-medium">{user.empName}</span>
          {unreadCount > 0 && (
            <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <p className="text-[10px] text-green-500">{isOnline ? 'Online' : 'Offline'}</p>
      </div>
    </button>
  )
);

ChatUserItem.displayName = 'ChatUserItem';

// Memoize the entire user list component
export const ChatUserList = memo(function ChatUserList({
  users,
  selectedUser,
  onSelectUser,
  onlineUsers,
  unreadCounts,
}: ChatUserListProps) {
  return (
    <div className="w-min border-r bg-gray-50 ">
      <div className="p-4">
        <h2 className="text-xl font-semibold text-gray-800">Messages</h2>
      </div>
      <div className="overflow-y-auto h-[calc(100vh-8rem)]">
        {users.map(user => (
          <ChatUserItem
            key={user.empUserId}
            user={user}
            isSelected={selectedUser?.empUserId === user.empUserId}
            isOnline={onlineUsers.has(user.empUserId)}
            unreadCount={unreadCounts[user.empUserId] || 0}
            onSelect={() => onSelectUser(user)}
          />
        ))}
      </div>
    </div>
  );
});
